<template>
  <div class="maps-popup" tabindex="-1" ref="popup">
    <div class="maps-popup__left">
      <img :src="thumbnailUrl || imgUrl" :class="imageClasses" />
      <div class="maps-popup__title">
        {{ cube.params.nickName }}
      </div>
      <div
        class="maps-popup__description"
        v-if="descriptionTextPosition === 'left'"
      >
        {{ descriptionText }}
      </div>
    </div>
    <div class="maps-popup__right">
      <div class="maps-popup__production-type" v-if="isProducer">
        {{ str(`productionTypes.${cube.productionType}`) }}<br />
      </div>
      <div class="maps-popup__capacity" v-if="isProducer">
        <div class="maps-popup__label">Leistung</div>
        {{ formatKWP(cube.params.productionCapacityKWp * 1000) }}

        <template
          v-if="
            (cube.params.inverterPublicURL || '').toString().length ||
            (cube.params.allowDisplayProductionDetails || '').toString() === '1'
          "
        >
          <div class="maps-popup__production-data">
            <template
              v-if="
                (cube.params.allowDisplayProductionDetails || '').toString() ===
                '1'
              "
            >
              <div class="maps-popup__label">Produktionsdaten</div>
            </template>

            <output
              class="maps-popup__production-data-table"
              v-if="
                (cube.params.allowDisplayProductionDetails || '').toString() ===
                '1'
              "
            >
              Aktuell:
              <strong>{{ formatWattage(cube.productionDetails.avg2H) }}</strong
              ><br />
              Überschuss gestern:
              <strong>{{ formatKWH(cube.productionDetails.prod1D) }}</strong
              ><br />
              Überschuss letztes Monat:
              <strong>{{ formatKWH(cube.productionDetails.prod1M) }}</strong
              ><br />
            </output>
            <output
              class="maps-popup__production-data-table__link"
              v-if="(cube.params.inverterPublicURL || '').toString().length"
              ><a
                :href="(cube.params.inverterPublicURL || '').toString()"
                target="_blank"
                >Produktionsdetails &raquo;</a
              ></output
            >
          </div>
        </template>
      </div>
      <div class="maps-popup__prices" v-if="isProducer">
        <output class="maps-popup__prices-current">
          <Price
            :label="str('efriends.currentPrice') + ' (brutto)'"
            :value="currentPrice"
          />
        </output>
        <output class="maps-popup__prices-current" v-if="nextPrice !== false">
          <Price :label="nextPriceLabel" :value="parseFloat(nextPrice)" />
        </output>
      </div>

      <div class="maps-popup__energy-lines" v-if="isProducer">
        <div class="maps-popup__label">Energylines</div>
        {{ cube.params.energyLinesRequired }} Energyline{{
          cube.params.energyLinesRequired > 1 ? 's' : ''
        }}
        <template v-if="cube.isProducer"> verwendet </template>
        <template v-else> benötigt </template>

        <template v-if="isProducer && notEnoughUnusedEnergyLines()">
          <div class="maps-popup__energy-lines-note">
            <template v-if="unusedEnergyLines > 0">
              Du hast nur noch {{ unusedEnergyLines }} freie Energyline{{
                unusedEnergyLines > 1 ? 's' : ''
              }}, und kannst von dieser Anlage noch keinen Strom beziehen.
            </template>
            <template v-else>
              Du hast keine freien Energylines mehr und kannst von keinen
              zusätzlichen Anlagen Strom beziehen.
            </template>
          </div>
        </template>
      </div>

      <div class="maps-popup__available-connections" v-if="isProducer">
        <template v-if="cube.params.openConnectionsAvailable > 0">
          {{ cube.params.openConnectionsAvailable }} Haushalt(e) können sich
          noch verbinden
        </template>
        <template v-else-if="cube.params.openConnectionsAvailable < 0">
          Demnächst verfügbar!
        </template>
        <template v-else>
          Alle verfügbaren Verbindungen zu dieser Anlage sind bereits belegt.
        </template>
      </div>

      <div
        class="maps-popup__description"
        v-if="descriptionTextPosition === 'main'"
      >
        {{ descriptionText }}
      </div>

      <div v-if="isConnected && !isMe" class="maps-popup__connections">
        <ConsumptionArrow
          classes="maps-popup__is-producer"
          type="positive"
          :angle="180"
          v-if="cube.isProducer"
        />
        <ConsumptionArrow
          classes="maps-popup__is-consumer"
          type="negative"
          v-if="cube.isConsumer && !cube.isDonatee"
        />
        <Heart
          :extra-classes="['maps-popup__donates']"
          v-if="cube.isDonatee || cube.isContributor"
        />
        <div class="maps-popup__connections-text">
          {{ connectionText }}
        </div>
      </div>

      <div class="maps-popup__connect" v-if="!isMe">
        <LinkButton
          v-if="
            cube.canReceiveDonations &&
            !cube.isDonatee &&
            $store.getters.hasProduction
          "
          @click.native="donateToCube"
          :text="str('efriends.doDonateButton')"
          :form="`donateToCube_${cube.cubeName}`"
          icon-left="Heart"
          :extra-classes="[
            'maps-popup__connect-button',
            'maps-popup__connect-button--donate',
          ]"
        />
        <LinkButton
          v-if="cube.canReceiveDonations && cube.isDonatee"
          @click.native="stopDonatingToCube"
          :text="str('efriends.stopDonatingButton')"
          :form="`stopDonatingToCube_${cube.cubeName}`"
          icon-left="Heart"
          :extra-classes="[
            'maps-popup__connect-button',
            'maps-popup__connect-button--stop-donating',
          ]"
        />
        <LinkButton
          v-if="canConnectToProducer"
          @click.native="connectToCube"
          :text="str('efriends.doConnectButton')"
          :form="`connectToCube_${cube.cubeName}`"
          icon-left="Bolt"
          :extra-classes="['maps-popup__connect-button']"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import ConsumptionArrow from '@/components/icons/ConsumptionArrow'
  import LinkButton from '@/components/LinkButton'
  import Price from '@/components/Price'
  import Heart from '@/components/icons/Heart'
  import util from '@/js/Util'

  export default {
    name: 'maps-popup',
    props: {
      cube: {
        type: Object,
        required: true,
      },
      // This is a hack to get the global vuex store available in here.
      // It's necessary because we do not add this component to the dom. Google
      // maps adds it where/when necessary.
      store: {
        type: Object,
        required: true,
      },
    },
    components: {
      ConsumptionArrow,
      Price,
      LinkButton,
      Heart,
    },
    data() {
      return {
        currentPrice: false,
        nextPrice: false,
        dateNextPrice: false,
        isProducer: false,
      }
    },
    created() {
      // Hack store back in to make the Price component work.
      // TODO Find a better implementation.
      this.$store = this.store

      if (this.cube.hasProduction) {
        const prices = this.$store.getters.producerPrices(this.cube.cubeName)
        this.isProducer = true
        this.currentPrice = prices.currentPrice
        if (prices.nextPrice) {
          this.nextPrice = prices.nextPrice
          this.dateNextPrice = prices.dateNextPrice
        }
      }
    },
    mounted() {
      setTimeout(() => {
        this.$refs.popup.focus()
      }, 0)
    },
    computed: {
      isMe() {
        return this.$store.getters.cubeName === this.cube.cubeName
      },
      nextPriceLabel: function () {
        return `Ab ${util.formatPriceDate(this.dateNextPrice)}`
      },
      canConnectToProducer: function () {
        const cube = this.cube
        if (!cube.hasProduction) {
          return false
        }

        if (cube.params.nickName === this.$store.getters.nickName) {
          return false
        }

        if (this.$store.getters.isProducerToMe(cube)) {
          // This cube is already a producer.
          return false
        }

        if (cube.params.openConnectionsAvailable < 1) {
          return false
        }

        if (this.notEnoughUnusedEnergyLines()) {
          return false
        }

        return true
      },
      isConnected: function () {
        const c = this.cube
        return c.isProducer || c.isConsumer || c.isDonatee || c.isContributor
      },
      imgUrl: function () {
        return this.$store.getters.cubeIconUrl(this.cube, 'svg')
      },
      connectionText: function () {
        if (this.cube.isDonatee) {
          if (this.cube.isProducer) {
            // The current cube donates power to and consumes from the other cube.
            return this.str(
              'efriends.mapsPopup.connectionTextDonateAndConsume',
              {
                '@cube': this.cube.params.nickName,
              }
            )
          } else {
            // The current cube donates power to the other cube.
            return this.str('efriends.mapsPopup.connectionTextDonate', {
              '@cube': this.cube.params.nickName,
            })
          }
        } else if (this.cube.isContributor) {
          if (this.cube.isConsumer) {
            // This cube donates power to us and consumes from us.
            return this.str(
              'efriends.mapsPopup.connectionTextContributesAndConsume',
              {
                '@cube': this.cube.params.nickName,
              }
            )
          } else {
            // This cube donates to us.
            return this.str('efriends.mapsPopup.connectionTextContributes', {
              '@cube': this.cube.params.nickName,
            })
          }
        } else {
          // No donating power in either direction.
          if (this.cube.isProducer && this.cube.isConsumer) {
            return this.str('efriends.mapsPopup.connectionTextBidirectional', {
              '@cube': this.cube.params.nickName,
            })
          } else if (this.cube.isProducer) {
            return this.str('efriends.mapsPopup.connectionTextProducer', {
              '@cube': this.cube.params.nickName,
            })
          } else if (this.cube.isConsumer) {
            return this.str('efriends.mapsPopup.connectionTextConsumer', {
              '@cube': this.cube.params.nickName,
            })
          } else {
            return ''
          }
        }
      },
      unusedEnergyLines: function () {
        return this.$store.getters.unusedEnergyLines
      },
      thumbnailUrl() {
        try {
          return this.cube.params.infoWindowImageDetails.thumbnail.url
        } catch (e) {
          return false
        }
      },
      imageClasses() {
        let result = ['maps-popup__img']
        if (this.thumbnailUrl) {
          result.push('maps-popup__img--thumbnail')
        }
        return result.join(' ')
      },
      descriptionText() {
        try {
          return this.cube.params.infoWindowText
        } catch (e) {
          return false
        }
      },
      descriptionTextPosition() {
        if (this.isProducer || this.cube.canReceiveDonations) {
          return 'left'
        }
        return 'main'
      },
    },
    methods: {
      notEnoughUnusedEnergyLines() {
        return (
          this.cube.params.energyLinesRequired >
          this.$store.getters.unusedEnergyLines
        )
      },
      connectToCube: function () {
        this.$store.dispatch('connectToCube', this.cube)
      },
      donateToCube: function () {
        this.$store.dispatch('donateToCube', this.cube)
      },
      stopDonatingToCube: function () {
        this.$store.dispatch('stopDonatingToCube', this.cube)
      },
    },
  }
</script>

<style lang="scss">
  .maps-popup {
    $popup-width: 500px;
    $img-width: 100px;
    $img-width-sm: 150px;
    $img-width-md: 200px;
    $text-color: $grey-darker;
    font-size: 0.75rem;
    color: $text-color;

    @media (min-width: $bp-ssm) {
      display: flex;
    }

    @media (min-width: $bp-md) {
      max-width: $popup-width;
      width: $popup-width;
      font-size: 0.875rem;
    }

    &__left {
      flex-shrink: 0;
      width: 100%;

      @media (min-width: $bp-ssm) {
        padding-right: spacer(1);
        flex-basis: $img-width-sm;
        max-width: $img-width-sm;
      }

      @media (min-width: $bp-md) {
        flex-basis: $img-width-md;
        max-width: $img-width-md;
      }
    }

    &__right {
      flex-shrink: 1;
    }

    &__img {
      display: block;
      width: 100%;
      height: auto;
      margin-bottom: spacer(0.5);
      margin-left: auto;
      margin-right: auto;

      .gm-style & {
        // We need to override this google style.
        max-height: 150px;
        width: auto;
      }
    }

    &__title {
      color: $grey-mediumdark;
      font-weight: 500;
      line-height: $line-height-min;
      margin-bottom: spacer(1);
      font-size: $font-size-lg;
      word-wrap: break-word;
      text-align: center;
    }

    &__description {
      margin-bottom: spacer(1);
    }

    &__label {
      color: $grey-darker;
      font-size: $font-size-base;
    }

    &__production-type,
    &__capacity,
    &__price,
    &__energy-lines {
      margin-bottom: spacer(0.5);
      line-height: $line-height-min;
      font-size: $font-size-base;
    }

    &__energy-lines {
      font-style: normal;

      &-note {
        margin-top: spacer(0.5);
      }
    }

    &__available-connections {
      margin: spacer(0.5) 0;
    }

    &__connections {
      display: flex;
      align-items: center;
      margin-top: spacer(1);

      &-text {
        margin-left: spacer(0.75);

        &-name {
          font-style: normal;
        }
      }

      .icon--positive,
      .icon--negative {
        margin-top: 0;
      }
    }

    &__is-producer {
      color: $color-negative;
      flex-basis: 1.5rem;
      flex-shrink: 0;
    }

    &__is-consumer {
      color: $color-positive;
      flex-basis: 1.5rem;
      flex-shrink: 0;
    }

    &__donates {
      color: $error-color;
      flex-basis: 1.5rem;
      flex-shrink: 0;
      margin-left: spacer(0.25);
    }

    &__connect {
      margin-top: spacer(1);

      @media (max-width: $bp-sm) {
        text-align: center;
      }

      &-button {
        width: 100%;

        &--donate,
        &--stop-donating {
          .icon--heart {
            margin-right: spacer(0.25);
          }
        }

        &--donate {
          background: $error-color;
          color: $white;

          &:hover,
          &:focus,
          &:active {
            background: darken($error-color, 5%);
            color: $white;
          }
        }

        &--stop-donating {
          background: $grey-lighter;
          color: $grey-darker;

          &:hover,
          &:focus,
          &:active {
            background: $grey-light;
            color: $grey-darker;
          }
        }

        + .button {
          margin-top: spacer(0.25);
        }
      }
    }

    &__connect-now {
      display: block;
      background: $community;
      margin-top: spacer(0.5);

      @include hover-focus-active {
        background: darken($community, 2%);
      }
    }

    &__prices {
      display: flex;
    }

    &__production-data {
      /*display: flex;*/
      margin-top: spacer(0.5);

      &-table {
        margin-top: spacer(0.5);
        //font-size: $font-size-base;

        &__link a {
          color: $grey-darker;
          text-decoration: underline;
        }
      }
    }

    .price {
      margin-bottom: spacer(0.5);
      min-width: 100px;

      &__label {
        color: $body-color;
        font-size: 100%;
      }

      &__value {
        line-height: $line-height-min;
        font-weight: 400;

        @media (min-width: $bp-sm) {
          font-size: $font-size-lg;
        }
      }
    }

    .button--primary {
      @media (max-width: $bp-sm - 1px) {
        padding-left: spacer(0.75);
        padding-right: spacer(0.75);
      }
    }
  }
</style>
